import { makeAutoObservable, runInAction } from "mobx";
import {
  createSwayyCatalogOption,
  createSwayyOptionValue,
  deleteSwayyOptionValue,
  getSwayyCatalogOption,
  listSwayyCatalogOptions,
  updateSwayyCatalogOption,
  updateSwayyOptionValue,
} from "../api/SwayyOptionsAPI";
import {
  isCancelledSwayyRequest,
  normalizeSwayyApiError,
  SwayyApiError,
} from "../api/swayyApiHelpers";
import { SwayyCatalogLoadState } from "../models/common/SwayyCatalogLoadState";
import {
  SwayyCatalogOption,
  SwayyOptionDisplayType,
  SwayyOptionValue,
} from "../models/options/SwayyCatalogOption";
import {
  SwayyUpdateOptionPayload,
  SwayyUpdateOptionValuePayload,
} from "../models/options/SwayyOptionPayloads";
import {
  addOptionValueFormSchema,
  createOptionFormSchema,
  validateAddOptionValueForm,
  zodFirstMessage,
} from "../validation/swayyFormSchemas";

export type SwayyCreateOptionForm = {
  name: string;
  displayType: SwayyOptionDisplayType;
};

export type SwayyAddOptionValueForm = {
  value: string;
  skuCode: string;
  hex: string;
};

export class SwayyCatalogOptionsStore {
  options: SwayyCatalogOption[] = [];
  optionDetail: SwayyCatalogOption | null = null;
  createForm: SwayyCreateOptionForm = {
    name: "",
    displayType: "dropdown",
  };
  addValueForm: SwayyAddOptionValueForm = {
    value: "",
    skuCode: "",
    hex: "",
  };
  editingValueId: number | null = null;
  listState: SwayyCatalogLoadState = "idle";
  detailState: SwayyCatalogLoadState = "idle";
  errorMessage = "";
  isSaving = false;
  searchDraft = "";
  search = "";
  private listAbort?: AbortController;
  private detailAbort?: AbortController;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get filteredOptions(): SwayyCatalogOption[] {
    const q = this.search.trim().toLowerCase();
    if (!q) return this.options;
    return this.options.filter(
      (option) =>
        option.name.toLowerCase().includes(q) ||
        String(option.displayType).toLowerCase().includes(q),
    );
  }

  get canCreateOption(): boolean {
    return createOptionFormSchema.safeParse(this.createForm).success;
  }

  get canAddOptionValue(): boolean {
    return addOptionValueFormSchema.safeParse(this.addValueForm).success;
  }

  setSearchDraft(value: string): void {
    this.searchDraft = value;
  }

  setSearch(value: string): void {
    this.search = value;
  }

  updateCreateForm<K extends keyof SwayyCreateOptionForm>(
    key: K,
    value: SwayyCreateOptionForm[K],
  ): void {
    this.createForm = { ...this.createForm, [key]: value };
  }

  resetCreateForm(): void {
    this.createForm = { name: "", displayType: "dropdown" };
  }

  updateAddValueForm<K extends keyof SwayyAddOptionValueForm>(
    key: K,
    value: SwayyAddOptionValueForm[K],
  ): void {
    this.addValueForm = { ...this.addValueForm, [key]: value };
  }

  resetAddValueForm(): void {
    this.addValueForm = { value: "", skuCode: "", hex: "" };
  }

  startEditValue(item: SwayyOptionValue): void {
    this.editingValueId = item.id;
    this.addValueForm = {
      value: item.name,
      skuCode: item.skuCode,
      hex: item.hex ?? "",
    };
  }

  cancelEditValue(): void {
    this.editingValueId = null;
    this.resetAddValueForm();
  }

  async loadOptions(): Promise<SwayyApiError | null> {
    this.listAbort?.abort();
    const controller = new AbortController();
    this.listAbort = controller;
    this.listState = "loading";
    this.errorMessage = "";
    try {
      const options = await listSwayyCatalogOptions(controller.signal);
      runInAction(() => {
        this.options = options;
        this.listState = "ready";
      });
      return null;
    } catch (error) {
      if (isCancelledSwayyRequest(error)) return null;
      const apiError = normalizeSwayyApiError(
        error,
        "Unable to load catalog options.",
      );
      runInAction(() => {
        this.listState = "error";
        this.errorMessage = apiError.message;
      });
      return apiError;
    }
  }

  async loadOption(
    optionId: number,
    options?: { silent?: boolean },
  ): Promise<SwayyApiError | null> {
    this.detailAbort?.abort();
    const controller = new AbortController();
    this.detailAbort = controller;
    if (!options?.silent) {
      this.detailState = "loading";
    }
    this.errorMessage = "";
    try {
      const detail = await getSwayyCatalogOption(optionId, controller.signal);
      runInAction(() => {
        this.optionDetail = detail;
        this.detailState = "ready";
      });
      return null;
    } catch (error) {
      if (isCancelledSwayyRequest(error)) return null;
      const apiError = normalizeSwayyApiError(error, "Unable to load option.");
      runInAction(() => {
        this.errorMessage = apiError.message;
        if (!options?.silent) {
          this.detailState = "error";
          this.optionDetail = null;
        }
      });
      return apiError;
    }
  }

  async createOption(): Promise<{
    optionId?: number;
    error?: SwayyApiError;
  }> {
    const parsed = createOptionFormSchema.safeParse(this.createForm);
    if (!parsed.success) {
      return { error: new SwayyApiError(zodFirstMessage(parsed.error)) };
    }
    this.isSaving = true;
    try {
      const created = await createSwayyCatalogOption({
        name: parsed.data.name,
        display_type: parsed.data.displayType,
      });
      runInAction(() => {
        this.isSaving = false;
        this.resetCreateForm();
      });
      await this.loadOptions();
      return { optionId: created.id };
    } catch (error) {
      const apiError = normalizeSwayyApiError(error, "Unable to create option.");
      runInAction(() => {
        this.isSaving = false;
      });
      return { error: apiError };
    }
  }

  async updateOption(
    optionId: number,
    payload: SwayyUpdateOptionPayload,
  ): Promise<SwayyApiError | null> {
    this.isSaving = true;
    try {
      const updated = await updateSwayyCatalogOption(optionId, payload);
      runInAction(() => {
        this.optionDetail = updated;
        this.isSaving = false;
      });
      return null;
    } catch (error) {
      const apiError = normalizeSwayyApiError(error, "Unable to update option.");
      runInAction(() => {
        this.isSaving = false;
      });
      return apiError;
    }
  }

  async createOptionValue(
    optionId: number,
    isSwatch: boolean,
  ): Promise<SwayyApiError | null> {
    const validated = validateAddOptionValueForm(this.addValueForm, isSwatch);
    if (!validated.ok) {
      return new SwayyApiError(validated.message);
    }
    this.isSaving = true;
    try {
      const value = this.addValueForm.value.trim();
      const skuCode = this.addValueForm.skuCode.trim();
      const hex = this.addValueForm.hex.trim();
      await createSwayyOptionValue(optionId, {
        value,
        sku_code: skuCode,
        meta: isSwatch && hex ? { hex } : null,
      });
      await this.loadOption(optionId);
      runInAction(() => {
        this.isSaving = false;
        this.resetAddValueForm();
      });
      return null;
    } catch (error) {
      const apiError = normalizeSwayyApiError(error, "Unable to create value.");
      runInAction(() => {
        this.isSaving = false;
      });
      return apiError;
    }
  }

  async updateValue(
    optionId: number,
    valueId: number,
    payload: SwayyUpdateOptionValuePayload,
  ): Promise<SwayyApiError | null> {
    this.isSaving = true;
    try {
      await updateSwayyOptionValue(optionId, valueId, payload);
      await this.loadOption(optionId, { silent: true });
      runInAction(() => {
        this.isSaving = false;
      });
      return null;
    } catch (error) {
      const apiError = normalizeSwayyApiError(error, "Unable to update value.");
      runInAction(() => {
        this.isSaving = false;
      });
      return apiError;
    }
  }

  async saveOptionValue(
    optionId: number,
    isSwatch: boolean,
  ): Promise<SwayyApiError | null> {
    try {
      if (this.editingValueId != null) {
        return await this.updateOptionValueFromForm(
          optionId,
          this.editingValueId,
          isSwatch,
        );
      }
      return await this.createOptionValue(optionId, isSwatch);
    } catch (error) {
      return normalizeSwayyApiError(error, "Unable to save value.");
    }
  }

  private async updateOptionValueFromForm(
    optionId: number,
    valueId: number,
    isSwatch: boolean,
  ): Promise<SwayyApiError | null> {
    const validated = validateAddOptionValueForm(this.addValueForm, isSwatch);
    if (!validated.ok) {
      return new SwayyApiError(validated.message);
    }

    const current = this.optionDetail?.values.find(
      (value) => value.id === valueId,
    );
    if (!current) {
      return new SwayyApiError("Option value not found.");
    }

    const value = this.addValueForm.value.trim();
    const skuCode = this.addValueForm.skuCode.trim();
    const hex = this.addValueForm.hex.trim();
    const payload: SwayyUpdateOptionValuePayload = {};

    if (value !== current.name) payload.value = value;
    if (skuCode !== current.skuCode) payload.sku_code = skuCode;
    if (isSwatch) {
      const currentHex = current.hex?.trim() || "";
      if (hex !== currentHex) {
        payload.meta = hex ? { hex } : null;
      }
    }

    if (Object.keys(payload).length === 0) {
      this.cancelEditValue();
      return null;
    }

    const error = await this.updateValue(optionId, valueId, payload);
    if (!error) this.cancelEditValue();
    return error;
  }

  async deleteOptionValue(
    optionId: number,
    valueId: number,
  ): Promise<SwayyApiError | null> {
    this.isSaving = true;
    try {
      await deleteSwayyOptionValue(optionId, valueId);
      await this.loadOption(optionId);
      runInAction(() => {
        this.isSaving = false;
      });
      return null;
    } catch (error) {
      const apiError = normalizeSwayyApiError(error, "Unable to delete value.");
      runInAction(() => {
        this.isSaving = false;
      });
      return apiError;
    }
  }

  dispose(): void {
    this.listAbort?.abort();
    this.detailAbort?.abort();
    this.cancelEditValue();
  }
}
